import { Button, Chip, debounce, Typography } from '@mui/material';
import { Box } from '@mui/system';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useState, useEffect } from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';

// Define the interface for the selected values that will be shown as chips
interface SelectedValue {
  field: string;
  value: string;
}

type MemberAdvancedSearchFormSummaryProps = {
  resetAll: () => void;
};

export const MemberAdvancedSearchFormSummary = (props: MemberAdvancedSearchFormSummaryProps) => {
  const { resetAll } = props;
  const values = useWatch();
  const { setValue } = useFormContext();
  const [selectedValues, setSelectedValues] = useState<SelectedValue[]>([]);

  // Special handling for truthy/falsy fields and checkboxes
  const isTruthyFieldType = (key) => ['hasNewspaper', 'hasMobilePhone', 'hasEmailAddress'].includes(key);
  const isCheckBoxType = (key) => ['includeResignedTitles', 'isFamilyMember', 'isMainMember'].includes(key);

  // Ignore fields that should not be a part of the form summary
  const isIgnoredFieldType = (key) =>
    ['status', 'pageSize', 'pageIndex', 'sortCol', 'sortDir', 'selectionId', 'selectionName', 'selectionInfo'].includes(key);

  const handleChipClick = (key, value) => {
    // Specific reset values based by field type, maybe overkill?
    const resetByField = () => {
      if (isTruthyFieldType(key)) return undefined;
      if (['resCause', 'sex'].includes(key)) return '';
      if (key === 'includeResignedTitles') return false;
      if (Array.isArray(value)) return '';
      if (key.indexOf('Date') >= 0) return null;
      return '';
    };

    setValue(key, resetByField()); // Update form state
    setSelectedValues((prevValues) => prevValues.filter((item) => item.field !== key)); // Remove from selected values (shown as chips)
  };

  const displayFormValue = (key, val) => {
    if (Array.isArray(val)) {
      return `(${val.length})`;
    }

    if (val !== undefined && ['resCause', 'sex'].includes(key)) {
      return val?.name;
    }

    if (val !== undefined && key.indexOf('Date') >= 0 && dayjs(val).isValid()) {
      return DateHelper.getDate(val);
    }

    if (isCheckBoxType(key)) {
      return t(`member:memberAdvancedSearch.${key}`);
    }

    if (isTruthyFieldType(key)) {
      return t(`member:memberAdvancedSearch.${key}.${val === 'true' ? 'truthy' : 'false'}`);
    }

    return val.toString();
  };

  const CustomChip = ({ field, value }: { field: string; value: any }) => {
    const titleSuffix = isTruthyFieldType(field);
    let chipTitle: string | JSX.Element = t(`member:memberAdvancedSearch.${field}${(titleSuffix && '.title') || ''}`);
    const { pickerHeader } = useDistrictPickerHeader();

    // Special cases
    if (field === 'districtIds') chipTitle = pickerHeader;
    if (isCheckBoxType(field)) chipTitle = '';

    return (
      <Chip
        color='primary'
        key={field}
        onDelete={() => handleChipClick(field, value)}
        label={
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, color: 'white', alignItems: 'center', mr: 1, pl: 0.5 }}>
            {chipTitle && (
              <Typography variant='body2' sx={{ fontWeight: 100 }}>
                {chipTitle}
              </Typography>
            )}
            <Typography variant='body2' sx={{ fontWeight: 600 }}>
              {displayFormValue(field, value)}
            </Typography>
          </Box>
        }
      />
    );
  };

  // Watch for form changes to update the summary
  useEffect(() => {
    const debouncedSetSelectedValues = debounce(() => {
      // Filter out empty state and some field.
      const selected: SelectedValue[] = Object.entries(values)
        .filter(([key, value]) => {
          if (isIgnoredFieldType(key)) return false;
          if (value !== undefined && value !== '' && isTruthyFieldType(key)) return true;
          if (key === 'includeResignedTitles' && value !== true) return false;
          if (Array.isArray(value) && value.length === 0) return false;
          if (value !== undefined && value !== '' && value !== null) return true;
          return false;
        })
        .map(([key, value]) => ({ field: key, value }));

      setSelectedValues(selected);
    }, 300); // Adjust the delay (300ms here) as needed

    debouncedSetSelectedValues();

    // Cleanup function to cancel debounce on component unmount or when values change
    return () => {
      debouncedSetSelectedValues.clear();
    };
  }, [values]);

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1, mt: 0.5, mb: 1 }}>
      {selectedValues
        .sort((a, b) => a.field.localeCompare(b.field))
        .map(({ field, value }) => (
          <CustomChip field={field} value={value} key={field} />
        ))}
      {selectedValues.length > 0 && (
        <Box>
          <Button variant='text' onClick={() => resetAll()}>
            {t('common:button.resetAll')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
